import { store } from "@store";
import { selectIsAnyDialogOpen } from "@store/dialogs/selectors";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { TTourEntry } from "./types";

interface IState {
  phase: "idle" | "tour";
  stepIdx: number;
  /** How the running tour was started; stamped on its analytics events. */
  entry?: TTourEntry;
}

interface IActions {
  startTour: (entry: TTourEntry) => void;
  setStepIdx: (idx: number) => void;
  endTour: () => void;
}

export type TTourStore = IState & IActions;

const IDLE: IState = { phase: "idle", stepIdx: 0 };

/**
 * `persistKey` opts a tour into surviving a page reload, per tab. Callers that
 * omit it keep the original in-memory behaviour.
 */
export const createTourStore = (persistKey?: string) => {
  const read = (): IState => {
    if (!persistKey) return IDLE;
    try {
      const raw = window.sessionStorage.getItem(persistKey);
      const saved: unknown = raw ? JSON.parse(raw) : null;
      if (
        saved &&
        typeof saved === "object" &&
        (saved as IState).phase === "tour" &&
        Number.isInteger((saved as IState).stepIdx) &&
        (saved as IState).stepIdx >= 0
      ) {
        return {
          phase: "tour",
          stepIdx: (saved as IState).stepIdx,
          entry: (saved as IState).entry,
        };
      }
    } catch {
      // storage can be unavailable; falling back to idle is always safe
    }
    return IDLE;
  };

  const write = (state: IState) => {
    if (!persistKey) return;
    try {
      if (state.phase === "idle") window.sessionStorage.removeItem(persistKey);
      else window.sessionStorage.setItem(persistKey, JSON.stringify(state));
    } catch {
      // ignore; persistence is a convenience, not a correctness requirement
    }
  };

  const useStore = create<TTourStore>((set, get) => ({
    ...read(),
    startTour: (entry) => {
      // The tour renders behind any open dialog and Escape can't end it while
      // a dialog is open, so starting it under one leaves it invisibly running.
      if (selectIsAnyDialogOpen(store.getState())) return;
      set({ phase: "tour", stepIdx: 0, entry });
      write({ phase: "tour", stepIdx: 0, entry });
    },
    setStepIdx: (stepIdx) => {
      set({ stepIdx });
      write({ phase: get().phase, stepIdx, entry: get().entry });
    },
    endTour: () => {
      set({ phase: "idle" });
      write(IDLE);
    },
  }));

  const useStoreShallow = <U>(selector: (s: TTourStore) => U) =>
    useStore(useShallow(selector));

  return { useStore, useStoreShallow };
};

export type TUseTourStoreShallow = ReturnType<
  typeof createTourStore
>["useStoreShallow"];
